import { useSelector } from '@tunasong/redux'
import type { FC } from 'react'
import { useEffect, useRef, useState } from 'react'
import { Dialog } from '../dialog/dialog.js'
import { Button } from '../primitives/button.js'
import { Snackbar } from '../primitives/snackbar.js'
import { conflictNoticeCopy as copy, conflictOffscreenMessage } from './conflict-notice-copy.js'
import { conflictPanels, uncoveredConflicts, useConflictPanels } from './conflict-panels.js'
import { EntityConflictBanner } from './entity-conflict-banner.js'
import styles from './conflict-snackbar-host.module.css'

interface Review {
  entityId: string
  fields: readonly string[]
  /** What had focus before Review, to return it to after a choice */
  returnFocus: HTMLElement | null
  /** Keep or Use their version was chosen; the dialog has closed, the status line and Undo stay a while */
  resolved: boolean
}

/** How long a resolved review stays mounted: its Undo snackbar's window (request-access spec §8.4) and then some */
const resolvedLinger = 7000

export interface ConflictSnackbarHostProps {
  /** The phone's bottom navigation is showing; the snackbar sits above it (GH #2713) */
  aboveBottomNavigation?: boolean
}

/** Where focus goes after a choice when what had it is gone: the page's main content (GH #2713) */
const focusMain = () => {
  const main = document.querySelector('main')
  if (!main) {
    return
  }
  if (!main.hasAttribute('tabindex')) {
    main.setAttribute('tabindex', '-1')
  }
  main.focus()
}

/**
 * The conflict notice when the panel that edits the held fields is off screen (GH #1912 option B,
 * docs/ux/2026-10-24-conflict-notice-ux.md, Layout: "Panel off screen"). Mount once, app-wide.
 *
 * A snackbar (string 12) names the held fields no visible banner shows, and stays until acted on. Its Review:
 *   - opens the panel when one can be opened (the settings and event sidebars), and that panel's banner takes focus;
 *   - otherwise opens a dialog, "Editing conflict", holding the same banner, with focus on "Keep my changes" (Joni,
 *     2026-10-24): the poll invite dialog, the playlist's Edit view, and fields with no panel.
 * Closing that dialog without choosing keeps the edit held, and the snackbar comes back. After a choice, focus
 * returns to what had it before Review.
 */
export const ConflictSnackbarHost: FC<ConflictSnackbarHostProps> = ({ aboveBottomNavigation = false }) => {
  const conflicts = useSelector(state => state.entities.conflicts)
  /** Re-render when banners and openers come and go */
  const panels = useConflictPanels()
  const [review, setReview] = useState<Review | null>(null)
  const lingerTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const reviewRef = useRef<HTMLButtonElement>(null)
  /**
   * GH #2713 P1: focus to place once the snackbar or dialog it was on has gone. 'review': on the returning snackbar's
   * Review (after a dismiss). 'choice': on Review if the snackbar returns, else on the main content.
   */
  const pendingFocus = useRef<'review' | 'choice' | null>(null)

  useEffect(
    () => () => {
      if (lingerTimer.current) {
        clearTimeout(lingerTimer.current)
      }
    },
    []
  )

  const uncovered = uncoveredConflicts(conflicts)
  const [first] = uncovered
  const reviewing = review !== null && !review.resolved
  /** GH #2713 P2: one notice at a time; a banner's "Switched to their version." toast goes first */
  const toastShowing = panels.some(panel => panel.toast)
  const snackbarOpen = Boolean(first) && !reviewing && !toastShowing

  useEffect(() => {
    if (!pendingFocus.current) {
      return
    }
    if (snackbarOpen && reviewRef.current) {
      reviewRef.current.focus()
      pendingFocus.current = null
      return
    }
    if (pendingFocus.current === 'choice' && !first && !reviewing) {
      focusMain()
      pendingFocus.current = null
    }
  }, [snackbarOpen, first, reviewing, review])

  const handleReview = () => {
    if (!first) {
      return
    }
    const open = conflictPanels.openerFor(first.entityId, first.fields)
    if (open) {
      conflictPanels.requestFocus(first.entityId, first.fields)
      open()
      return
    }
    const returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null
    setReview({ entityId: first.entityId, fields: first.fields, returnFocus, resolved: false })
  }

  /** Closed without a choice: nothing is resolved, so the snackbar comes back, and its Review takes focus */
  const handleDismiss = () => {
    pendingFocus.current = 'review'
    setReview(null)
  }

  /** Focus returns to what had it before Review; when that was Review itself, which has gone, see `pendingFocus` */
  const handleResolved = () => {
    const returnFocus = review?.returnFocus
    if (returnFocus?.isConnected) {
      returnFocus.focus()
    } else {
      pendingFocus.current = 'choice'
    }
    setReview(current => (current ? { ...current, resolved: true } : current))
    lingerTimer.current = setTimeout(() => setReview(null), resolvedLinger)
  }

  return (
    <>
      {/*
       * GH #2713 P2: a live region announces a change to content inside it, and the Snackbar primitive mounts its region
       * with the message already in place. This one is always mounted, so the message is a change it announces; the
       * snackbar itself is not a second live region.
       */}
      <div data-conflict-announcer="" aria-live="polite" className={styles.visuallyHidden}>
        {snackbarOpen && first ? conflictOffscreenMessage(first.fields) : ''}
      </div>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={null}
        role={undefined}
        aria-live="off"
        className={aboveBottomNavigation ? styles.aboveNavigation : undefined}
        message={first ? conflictOffscreenMessage(first.fields) : undefined}
        action={
          <Button ref={reviewRef} size="small" onClick={handleReview}>
            {copy.review}
          </Button>
        }
      />
      {review ? (
        <EntityConflictBanner
          key={`${review.entityId}:${review.fields.join(',')}`}
          entityId={review.entityId}
          fields={review.fields}
          focus="keep"
          labelled={false}
          onResolved={handleResolved}
          wrap={banner => (
            <Dialog
              open={!review.resolved && Boolean(banner)}
              title={copy.region}
              maxWidth="sm"
              onClose={handleDismiss}
            >
              {banner}
            </Dialog>
          )}
        />
      ) : null}
    </>
  )
}

export default ConflictSnackbarHost
